import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import { getOrders, updateOrderStatus, updateOrderPhone, updateRetailOrder, deleteCancelledOrder, markBalanceReceived, getOrderStats, downloadOrderReceipt, downloadPdfFromBase64, getShopInquiryCount } from '../utils/api';
import { downloadJpegPagesFromBase64 } from '../utils/orderReceiptDownload';
import OrdersToolbar from './OrdersToolbar';
import OrderListRow from './OrderListRow';
import OrderListHeader from './OrderListHeader';
import ShopInquiriesPanel from './ShopInquiriesPanel';
import RetailOrderEditPanel from './RetailOrderEditPanel';

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [lastOrderCount, setLastOrderCount] = useState(0);

  // Filters and pagination
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: '',
    sort: 'newest',
    fromDate: '',
    toDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Confirmation dialog states
  const [confirmOrderToDispatch, setConfirmOrderToDispatch] = useState(null);
  const [showDispatchConfirmation, setShowDispatchConfirmation] = useState(false);
  const [confirmOrderToDispatchWholesale, setConfirmOrderToDispatchWholesale] = useState(null);
  const [showWholesaleDispatchConfirmation, setShowWholesaleDispatchConfirmation] = useState(false);
  const [confirmOrderToCancel, setConfirmOrderToCancel] = useState(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [confirmOrderToDelete, setConfirmOrderToDelete] = useState(null);
  const [showDeleteCancelledConfirmation, setShowDeleteCancelledConfirmation] = useState(false);
  const [deletingCancelled, setDeletingCancelled] = useState(false);
  const [confirmOrderForBalanceReceived, setConfirmOrderForBalanceReceived] = useState(null);
  const [showBalanceReceivedConfirmation, setShowBalanceReceivedConfirmation] = useState(false);
  const [orderTab, setOrderTab] = useState('active');
  const [pendingInquiryCount, setPendingInquiryCount] = useState(0);

  // Order expansion states
  const [expandedOrders, setExpandedOrders] = useState({});

  // Image viewer states
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageViewer, setShowImageViewer] = useState(false);

  // Add mobile for orders created without phone
  const [addingPhoneKey, setAddingPhoneKey] = useState(null);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);

  // Retail order edit panel
  const [editingOrder, setEditingOrder] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Fetch orders
  const fetchPendingInquiryCount = useCallback(async () => {
    try {
      const data = await getShopInquiryCount('pending');
      setPendingInquiryCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching inquiry count:', error);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    if (orderTab === 'inquiries') {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const query = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      };

      if (orderTab === 'cancelled') {
        query.status = 'cancelled';
        delete query.excludeCancelled;
      } else {
        query.excludeCancelled = true;
        if (query.status === 'cancelled') {
          delete query.status;
        }
      }

      const data = await getOrders(query);

      // Sanitize orders data to remove any Error objects
      const sanitizedOrders = safeSanitizeOrders(data.orders);
      setOrders(sanitizedOrders);
      setExpandedOrders((prev) => {
        const next = {};
        sanitizedOrders.forEach((order) => {
          if (prev[order._id]) {
            next[order._id] = true;
          }
        });
        return next;
      });
      setPagination(prev => ({
        ...prev,
        total: data.total,
        totalPages: data.totalPages
      }));
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to load orders';
      toast.error(message);
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, orderTab]);

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const data = await getOrderStats();
      // Sanitize stats data to remove any Error objects
      const sanitizedStats = safeSanitizeStats(data);
      setStats(sanitizedStats);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Don't show toast for stats errors - they're not critical
    }
  };

  // Update order status
  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const result = await updateOrderStatus(orderId, newStatus);
      toast.success(result.message);

      // Update local state with safe values
      if (newStatus === 'cancelled' && orderTab === 'active') {
        setOrders(orders.filter((order) => order._id !== orderId));
      } else {
        setOrders(orders.map(order =>
          order._id === orderId
            ? {
                ...order,
                status: safeRender(result.order?.status, newStatus),
                balance: safeRender(result.order?.balance, order.balance),
                totalAdvance: safeRender(result.order?.totalAdvance, order.totalAdvance),
              }
            : order
        ));
      }

      // Refresh stats
      fetchStats();
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to update order status';
      toast.error(message);
      console.error('Error updating order status:', error);
    }
  };

  // Mark balance as received
  const handleMarkBalanceReceived = async (orderId) => {
    try {
      const result = await markBalanceReceived(orderId);
      toast.success(result.message);

      // Update local state
      setOrders(orders.map(order =>
        order._id === orderId
          ? { ...order, balance: 0, totalAdvance: result.order.totalAdvance }
          : order
      ));

      // Refresh stats
      fetchStats();
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to mark balance as received';
      toast.error(message);
      console.error('Error marking balance as received:', error);
    }
  };

  const handleWholesaleBalanceReceived = (order) => {
    const safeOrder = {
      ...order,
      orderNo: safeRender(order.orderNo),
      customerName: safeRender(order.customerName),
      balance: safeRender(order.balance, 0),
      grandTotal: safeRender(order.grandTotal, 0),
      totalAdvance: safeRender(order.totalAdvance, 0),
      phone: safeRender(order.phone),
      type: safeRender(order.type),
      status: safeRender(order.status),
    };
    setConfirmOrderForBalanceReceived(safeOrder);
    setShowBalanceReceivedConfirmation(true);
  };

  const confirmWholesaleBalanceReceived = async (confirmed) => {
    if (!confirmed || !confirmOrderForBalanceReceived) {
      setShowBalanceReceivedConfirmation(false);
      setConfirmOrderForBalanceReceived(null);
      return;
    }

    await handleMarkBalanceReceived(confirmOrderForBalanceReceived._id);
    await handleUpdateOrderStatus(confirmOrderForBalanceReceived._id, 'completed');

    setShowBalanceReceivedConfirmation(false);
    setConfirmOrderForBalanceReceived(null);
  };

  const confirmDispatchWholesaleOrder = async (confirmed) => {
    if (!confirmed || !confirmOrderToDispatchWholesale) {
      setShowWholesaleDispatchConfirmation(false);
      setConfirmOrderToDispatchWholesale(null);
      return;
    }

    await handleUpdateOrderStatus(confirmOrderToDispatchWholesale._id, 'dispatched');

    setShowWholesaleDispatchConfirmation(false);
    setConfirmOrderToDispatchWholesale(null);
  };

  // Handle retail order dispatch confirmation
  const handleDispatchRetailOrder = (order) => {
    // Ensure order data is safe before storing in state
    const safeOrder = {
      ...order,
      orderNo: safeRender(order.orderNo),
      customerName: safeRender(order.customerName),
      balance: safeRender(order.balance, 0),
      grandTotal: safeRender(order.grandTotal, 0),
      totalAdvance: safeRender(order.totalAdvance, 0),
      phone: safeRender(order.phone),
      type: safeRender(order.type),
      status: safeRender(order.status)
    };
    setConfirmOrderToDispatch(safeOrder);
    setShowDispatchConfirmation(true);
  };

  // Handle order cancellation confirmation
  const handleCancelOrder = (order) => {
    // Ensure order data is safe before storing in state
    const safeOrder = {
      ...order,
      orderNo: safeRender(order.orderNo),
      customerName: safeRender(order.customerName),
      balance: safeRender(order.balance, 0),
      grandTotal: safeRender(order.grandTotal, 0),
      totalAdvance: safeRender(order.totalAdvance, 0),
      phone: safeRender(order.phone),
      type: safeRender(order.type),
      status: safeRender(order.status),
      items: safeRender(order.items, [])
    };
    setConfirmOrderToCancel(safeOrder);
    setShowCancelConfirmation(true);
  };

  // Confirm order cancellation
  const confirmCancelOrder = async (confirmed) => {
    if (!confirmed || !confirmOrderToCancel) {
      setShowCancelConfirmation(false);
      setConfirmOrderToCancel(null);
      return;
    }

    try {
      await handleUpdateOrderStatus(confirmOrderToCancel._id, 'cancelled');
      toast.success('❌ Order cancelled successfully! Inventory has been updated.');
    } catch (error) {
      console.error('Error cancelling order:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to cancel order';
      toast.error(errorMessage);
    }

    setShowCancelConfirmation(false);
    setConfirmOrderToCancel(null);
  };

  const handleDeleteCancelledOrder = (order) => {
    if (String(order?.status || '').toLowerCase() !== 'cancelled') return;
    setConfirmOrderToDelete(order);
    setShowDeleteCancelledConfirmation(true);
  };

  const confirmDeleteCancelledOrder = async (confirmed) => {
    if (!confirmed || !confirmOrderToDelete) {
      setShowDeleteCancelledConfirmation(false);
      setConfirmOrderToDelete(null);
      return;
    }

    setDeletingCancelled(true);
    try {
      const result = await deleteCancelledOrder(confirmOrderToDelete._id);
      setOrders((prev) => prev.filter((o) => String(o._id) !== String(confirmOrderToDelete._id)));
      setExpandedOrders((prev) => {
        const next = { ...prev };
        delete next[confirmOrderToDelete._id];
        return next;
      });
      fetchStats();
      toast.success(
        `Order ${result.orderNo || confirmOrderToDelete.orderNo} deleted. Advance is no longer counted as retained.`
      );
    } catch (error) {
      toast.error(error.message || 'Failed to delete order');
    } finally {
      setDeletingCancelled(false);
      setShowDeleteCancelledConfirmation(false);
      setConfirmOrderToDelete(null);
    }
  };

  // Confirm retail order dispatch
  const confirmDispatchRetailOrder = async (confirmed) => {
    if (!confirmed || !confirmOrderToDispatch) {
      setShowDispatchConfirmation(false);
      setConfirmOrderToDispatch(null);
      return;
    }

    try {
      // Dispatch the retail order - server will auto-complete and mark balance as received
      await handleUpdateOrderStatus(confirmOrderToDispatch._id, 'dispatched');
    } catch (error) {
      console.error('Error dispatching retail order:', error);
      // Ensure error is a string before displaying
      const errorMessage = error.response?.data?.message || error.message || 'Failed to dispatch order';
      toast.error(errorMessage);
    }

    setShowDispatchConfirmation(false);
    setConfirmOrderToDispatch(null);
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      status: '',
      type: '',
      search: '',
      sort: 'newest',
      fromDate: '',
      toDate: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleOrderTabChange = (tab) => {
    setOrderTab(tab);
    setFilters((prev) => ({ ...prev, status: '' }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchOrders();
    fetchPendingInquiryCount();
  }, [fetchOrders, fetchPendingInquiryCount]);

  // Refetch when filters or pagination change
  useEffect(() => {
    fetchOrders();
  }, [filters, pagination.page, pagination.limit, fetchOrders]);

  // Real-time notifications for new orders
  useEffect(() => {
    const checkForNewOrders = async () => {
      try {
        const data = await getOrderStats();
        const currentOrderCount = data.totalOrders;

        if (lastOrderCount > 0 && currentOrderCount > lastOrderCount) {
          const newOrders = currentOrderCount - lastOrderCount;
          toast.success(`🔔 ${newOrders} new order${newOrders > 1 ? 's' : ''} received!`, {
            duration: 5000,
            style: {
              background: '#10B981',
              color: 'white',
              fontSize: '16px'
            }
          });
          // Refresh the orders list
          fetchOrders();
        }

        setLastOrderCount(currentOrderCount);
      } catch (error) {
        console.error('Error checking for new orders:', error);
      }
    };

    // Check immediately, then every 30 seconds
    checkForNewOrders();
    const interval = setInterval(checkForNewOrders, 30000);

    return () => clearInterval(interval);
  }, [lastOrderCount, fetchOrders]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getDisplayStatus = (order) => safeRender(order.status, 'pending');

  const getStatusText = (order) => {
    const status = getDisplayStatus(order);
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getBalanceDueLabel = (order) => {
    const balance = safeRender(order.balance, 0);
    if (balance <= 0) return null;
    return `₹${Number(balance).toLocaleString('en-IN')} due`;
  };

  const downloadReceipt = async (orderId, orderNo, lang, format = 'jpeg') => {
    const loadingId = toast.loading(`Generating ${lang} receipt…`);
    try {
      const langKey = lang.toLowerCase();
      const result = await downloadOrderReceipt(orderId, langKey, format);
      toast.dismiss(loadingId);

      if (format === 'jpeg') {
        const jpegPages = result.jpegBase64Pages
          || (langKey === 'kn' ? result.jpegBase64PagesKn : result.jpegBase64PagesMr);
        if (jpegPages?.length) {
          downloadJpegPagesFromBase64(jpegPages, `Receipt_${orderNo}_${lang}`);
          toast.success(`${lang} receipt downloaded (JPEG)`, { duration: 3000 });
          return;
        }
        const pdfContent = result.pdfBase64
          || (langKey === 'kn' ? result.pdfBase64Kn : result.pdfBase64Mr);
        if (pdfContent) {
          downloadPdfFromBase64(pdfContent, `Receipt_${orderNo}_${lang}.pdf`);
          toast.success(`${lang} receipt downloaded (PDF fallback)`, { duration: 3000 });
          return;
        }
      } else {
        const pdfContent = result.pdfBase64
          || (langKey === 'kn' ? result.pdfBase64Kn : result.pdfBase64Mr);
        if (pdfContent) {
          downloadPdfFromBase64(pdfContent, `Receipt_${orderNo}_${lang}.pdf`);
          toast.success(`${lang} receipt downloaded (PDF)`, { duration: 3000 });
          return;
        }
      }

      toast.error(`${lang} receipt not available`);
    } catch (error) {
      toast.dismiss(loadingId);
      console.error('Error downloading receipt:', error);
      const message = error.response?.data?.message || error.message || 'Failed to download receipt';
      toast.error(message);
    }
  };

  // Safe rendering utility to prevent Error objects from being rendered
  const safeRender = (value, fallback = 'N/A') => {
    try {
      if (value instanceof Error) {
        console.error('🔴 SAFE RENDER: Error object detected:', value.message, value.stack);
        return fallback;
      }
      // For arrays, ensure we return an array (check if value is actually an array)
      if (Array.isArray(value)) {
        return value;
      }
      // For other objects, return as-is
      if (typeof value === 'object' && value !== null && typeof value !== 'string') {
        return value;
      }
      // For strings and other primitives, return the value or fallback
      return value !== undefined && value !== null ? value : fallback;
    } catch (error) {
      console.error('🔴 SAFE RENDER: Error in safeRender function:', error);
      return fallback;
    }
  };

  // Deep sanitization utility to remove Error objects from order data
  const safeSanitizeOrders = (orders) => {
    if (!Array.isArray(orders)) {
      console.error('🔴 SANITIZE: Expected array, got:', typeof orders);
      return [];
    }

    return orders
      .filter(order => {
        if (order instanceof Error) {
          console.error('🔴 SANITIZE: Filtered out Error object from orders array:', order.message);
          return false;
        }
        return true;
      })
      .map(order => {
        if (!order || typeof order !== 'object') {
          console.error('🔴 SANITIZE: Invalid order object:', order);
          return null;
        }

        const sanitizedOrder = {};

        // Sanitize each property of the order
        Object.keys(order).forEach(key => {
          const value = order[key];

          if (value instanceof Error) {
            console.error(`🔴 SANITIZE: Error object in order.${key}:`, value.message);
            sanitizedOrder[key] = null;
          } else if (Array.isArray(value)) {
            // Sanitize arrays (like items array)
            sanitizedOrder[key] = value
              .filter(item => {
                if (item instanceof Error) {
                  console.error('🔴 SANITIZE: Filtered out Error object from array:', item.message);
                  return false;
                }
                return true;
              })
              .map(item => {
                if (!item || typeof item !== 'object') return item;

                const sanitizedItem = {};
                Object.keys(item).forEach(itemKey => {
                  if (item[itemKey] instanceof Error) {
                    console.error(`🔴 SANITIZE: Error object in item.${itemKey}:`, item[itemKey].message);
                    sanitizedItem[itemKey] = null;
                  } else {
                    sanitizedItem[itemKey] = item[itemKey];
                  }
                });
                return sanitizedItem;
              });
          } else {
            sanitizedOrder[key] = value;
          }
        });

        return sanitizedOrder;
      })
      .filter(order => order !== null);
  };

  // Sanitization utility for stats data
  const safeSanitizeStats = (stats) => {
    if (!stats || typeof stats !== 'object') {
      console.error('🔴 SANITIZE STATS: Invalid stats object:', stats);
      return {};
    }

    const sanitizedStats = {};

    Object.keys(stats).forEach(key => {
      const value = stats[key];

      if (value instanceof Error) {
        console.error(`🔴 SANITIZE STATS: Error object in stats.${key}:`, value.message);
        sanitizedStats[key] = 0; // Default to 0 for stats
      } else if (Array.isArray(value)) {
        // Sanitize arrays (like statusBreakdown)
        sanitizedStats[key] = value
          .filter(item => {
            if (item instanceof Error) {
              console.error('🔴 SANITIZE STATS: Filtered out Error object from stats array:', item.message);
              return false;
            }
            return true;
          })
          .map(item => {
            if (!item || typeof item !== 'object') return item;

            const sanitizedItem = {};
            Object.keys(item).forEach(itemKey => {
              if (item[itemKey] instanceof Error) {
                console.error(`🔴 SANITIZE STATS: Error object in stats item.${itemKey}:`, item[itemKey].message);
                sanitizedItem[itemKey] = 0;
              } else {
                sanitizedItem[itemKey] = item[itemKey];
              }
            });
            return sanitizedItem;
          });
      } else {
        sanitizedStats[key] = value;
      }
    });

    return sanitizedStats;
  };

  const handleEditRetailOrder = (order) => {
    if (String(order?.type || '').toLowerCase() !== 'retail') return;
    if (String(order?.status || '').toLowerCase() !== 'pending') return;
    setEditingOrder(order);
  };

  const handleSaveRetailEdit = async (payload) => {
    if (!editingOrder?._id) return;
    setEditSaving(true);
    try {
      const result = await updateRetailOrder(editingOrder._id, payload);
      const updated = result.order;
      setOrders((prev) =>
        prev.map((order) =>
          String(order._id) === String(updated._id) ? { ...order, ...updated } : order
        )
      );
      toast.success(`Order ${updated.orderNo || editingOrder.orderNo} updated`);
      setEditingOrder(null);
    } catch (error) {
      toast.error(error.message || 'Failed to update order');
    } finally {
      setEditSaving(false);
    }
  };

  const isPhoneMissing = (phone) => !String(phone || '').trim();

  const handleStartAddPhone = (customerKey) => {
    setAddingPhoneKey(customerKey);
    setPhoneDraft('');
  };

  const handleCancelAddPhone = () => {
    setAddingPhoneKey(null);
    setPhoneDraft('');
    setPhoneSaving(false);
  };

  const handleSaveAddPhone = async (customerData) => {
    const trimmed = phoneDraft.replace(/\D/g, '').slice(0, 10);
    if (!/^[6-9]\d{9}$/.test(trimmed)) {
      toast.error('Please enter a valid 10-digit Indian mobile number');
      return;
    }

    const targets = (customerData.orders || []).filter((order) => isPhoneMissing(order.phone));
    if (targets.length === 0) {
      handleCancelAddPhone();
      return;
    }

    setPhoneSaving(true);
    try {
      const results = await Promise.all(
        targets.map((order) => updateOrderPhone(order._id, trimmed))
      );
      const updatedById = new Map(
        results.map((result) => [String(result.order._id), result.order])
      );
      setOrders((prev) =>
        prev.map((order) => {
          const updated = updatedById.get(String(order._id));
          return updated ? { ...order, phone: updated.phone } : order;
        })
      );
      toast.success(
        targets.length === 1
          ? 'Mobile number added'
          : `Mobile number added to ${targets.length} orders`
      );
      handleCancelAddPhone();
    } catch (error) {
      toast.error(error.message || 'Failed to add mobile number');
      setPhoneSaving(false);
    }
  };

  // Group orders by customer for better organization
  const groupOrdersByCustomer = (orders) => {
    const grouped = {};
    orders.forEach(order => {
      const customerKey = `${safeRender(order.customerName)}-${safeRender(order.phone)}`;
      if (!grouped[customerKey]) {
        grouped[customerKey] = {
          customerName: safeRender(order.customerName),
          phone: safeRender(order.phone),
          orders: []
        };
      }
      grouped[customerKey].orders.push(order);
    });
    return grouped;
  };

  const handleDispatchWholesale = (order) => {
    const balance = safeRender(order.balance, 0);
    if (balance > 0) {
      setConfirmOrderToDispatchWholesale({
        ...order,
        orderNo: safeRender(order.orderNo),
        customerName: safeRender(order.customerName),
        balance,
        grandTotal: safeRender(order.grandTotal, 0),
        totalAdvance: safeRender(order.totalAdvance, 0),
        phone: safeRender(order.phone),
        type: safeRender(order.type),
        status: safeRender(order.status),
      });
      setShowWholesaleDispatchConfirmation(true);
      return;
    }

    handleUpdateOrderStatus(order._id, 'dispatched');
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setShowImageViewer(true);
  };

  const groupedOrders = groupOrdersByCustomer(orders);
  const hasMultipleCustomers = Object.keys(groupedOrders).length > 1;
  const cancelledOrderCount = safeRender(
    stats.statusBreakdown?.find((item) => item._id === 'cancelled')?.count,
    0
  );

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const renderOrderList = () => {
    let rowIndex = 0;

    return Object.keys(groupedOrders).map((customerKey) => {
      const customerData = groupedOrders[customerKey];
      const hasMultipleOrders = customerData.orders.length > 1;

      const rows = customerData.orders.map((order) => {
        const currentRowIndex = rowIndex;
        rowIndex += 1;

        return (
        <OrderListRow
          key={order._id}
          order={order}
          rowIndex={currentRowIndex}
          expanded={Boolean(expandedOrders[order._id])}
          onToggle={toggleOrderExpansion}
          safeRender={safeRender}
          formatDate={formatDate}
          getDisplayStatus={getDisplayStatus}
          getStatusText={getStatusText}
          getBalanceDueLabel={getBalanceDueLabel}
          onDispatchRetail={handleDispatchRetailOrder}
          onDispatchWholesale={handleDispatchWholesale}
          onCancel={handleCancelOrder}
          onEdit={handleEditRetailOrder}
          onDeleteCancelled={orderTab === 'cancelled' ? handleDeleteCancelledOrder : undefined}
          onMarkBalanceReceived={handleMarkBalanceReceived}
          onWholesaleBalanceReceived={handleWholesaleBalanceReceived}
          onDownloadReceipt={downloadReceipt}
          onImageClick={handleImageClick}
          readOnly={orderTab === 'cancelled'}
        />
        );
      });

      return (
        <div key={customerKey} className="bg-white rounded-lg shadow-sm overflow-visible">
          <div className="order-customer-strip">
            <span className="order-customer-strip-name">{customerData.customerName}</span>
            <span className="order-customer-strip-sep" aria-hidden="true">·</span>
            {orderTab !== 'cancelled' && isPhoneMissing(customerData.phone) ? (
              addingPhoneKey === customerKey ? (
                <span className="inline-flex flex-wrap items-center gap-1.5 min-w-0">
                  <input
                    type="tel"
                    value={phoneDraft}
                    onChange={(e) => setPhoneDraft(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="h-7 w-[8.5rem] px-2 text-xs border border-gray-300 rounded-md bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    placeholder="10-digit mobile"
                    disabled={phoneSaving}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => handleSaveAddPhone(customerData)}
                    disabled={phoneSaving}
                    className="h-7 px-2 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {phoneSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelAddPhone}
                    disabled={phoneSaving}
                    className="h-7 px-2 text-xs font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleStartAddPhone(customerKey)}
                  className="order-customer-strip-phone text-indigo-600 hover:text-indigo-800 underline underline-offset-2 font-medium"
                >
                  Add mobile
                </button>
              )
            ) : (
              <span className="order-customer-strip-phone">
                {isPhoneMissing(customerData.phone) ? '—' : customerData.phone}
              </span>
            )}
            {hasMultipleOrders && (
              <>
                <span className="order-customer-strip-sep" aria-hidden="true">·</span>
                <span className="order-customer-strip-meta">{customerData.orders.length} orders</span>
              </>
            )}
          </div>
          {rows}
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 py-1 md:py-4">
      <Toaster position="top-right" />
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="shrink-0 mb-1">
            <div className="orders-page-header flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 lg:gap-4">
              <div className="shrink-0 min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-gray-800">
                  Order Management
                </h1>
                <p className="mt-0.5 text-xs text-gray-500 hidden md:block lg:hidden">
                  {orderTab === 'inquiries'
                    ? 'Review shop inquiries and create orders after UPI advance'
                    : orderTab === 'cancelled'
                      ? 'View cancelled orders and retained advance'
                      : hasMultipleCustomers
                        ? 'Manage and track orders grouped by customers'
                        : 'Manage and track active orders'}
                </p>
              </div>

              <div className="flex flex-wrap gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={() => handleOrderTabChange('active')}
              className={`min-h-11 sm:min-h-10 px-2.5 sm:px-3 rounded-lg text-sm font-medium transition-colors ${
                orderTab === 'active'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              Active Orders
            </button>
            <button
              type="button"
              onClick={() => handleOrderTabChange('cancelled')}
              className={`min-h-11 sm:min-h-10 px-2.5 sm:px-3 rounded-lg text-sm font-medium transition-colors ${
                orderTab === 'cancelled'
                  ? 'bg-red-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <span className="sm:hidden">
                Cancelled{cancelledOrderCount > 0 ? ` (${cancelledOrderCount})` : ''}
              </span>
              <span className="hidden sm:inline">
                Cancelled Orders{cancelledOrderCount > 0 ? ` (${cancelledOrderCount})` : ''}
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleOrderTabChange('inquiries')}
              className={`min-h-11 sm:min-h-10 px-2.5 sm:px-3 rounded-lg text-sm font-medium transition-colors ${
                orderTab === 'inquiries'
                  ? 'bg-amber-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              } ${pendingInquiryCount > 0 && orderTab !== 'inquiries' ? 'admin-inquiry-tab--pulse ring-1 ring-amber-300' : ''}`}
            >
              <span className="sm:hidden inline-flex items-center gap-1.5">
                Inquiries
                {pendingInquiryCount > 0 ? (
                  <span className={`admin-inquiry-badge admin-inquiry-badge--pulse ${orderTab === 'inquiries' ? '!bg-white !text-amber-700' : ''}`}>
                    {pendingInquiryCount}
                  </span>
                ) : null}
              </span>
              <span className="hidden sm:inline-flex items-center gap-1.5">
                Shop Inquiries
                {pendingInquiryCount > 0 ? (
                  <span className={`admin-inquiry-badge admin-inquiry-badge--pulse ${orderTab === 'inquiries' ? '!bg-white !text-amber-700' : ''}`}>
                    {pendingInquiryCount}
                  </span>
                ) : null}
              </span>
            </button>
              </div>
            </div>
          </div>

          {orderTab !== 'inquiries' ? (
            <OrdersToolbar
              stats={stats}
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              safeRender={safeRender}
              orderTab={orderTab}
            />
          ) : null}

          {orderTab === 'inquiries' ? (
            <ShopInquiriesPanel onCountChange={fetchPendingInquiryCount} />
          ) : (
            <>
              {/* Orders Display - Grouped by Customer */}
              <div className="space-y-4">
                {loading ? (
                  <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-600">
                    Loading orders...
                  </div>
                ) : orders.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-600">
                    No orders found
                  </div>
                ) : (
                  <>
                    <div className="order-list-sticky-header hidden md:block">
                      <OrderListHeader />
                    </div>
                    <div className="space-y-4">
                      {renderOrderList()}
                    </div>
                  </>
                )}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
            <div className="flex flex-wrap justify-center gap-2 overflow-x-auto max-w-full px-2 mt-6">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="min-h-11 sm:h-8 px-3 sm:px-2.5 text-sm rounded-md bg-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <span className="flex sm:hidden items-center text-sm text-gray-600 px-2 min-h-11">
                Page {pagination.page} of {pagination.totalPages}
              </span>

              <div className="hidden sm:flex items-center gap-2">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    const current = pagination.page;
                    return page === 1 || page === pagination.totalPages ||
                           (page >= current - 1 && page <= current + 1);
                  })
                  .map((page, index, array) => (
                    <div key={page} className="flex items-center">
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      <button
                        onClick={() => handlePageChange(page)}
                        className={`h-8 px-2.5 text-sm rounded-md ${
                          pagination.page === page
                            ? 'admin-btn admin-btn-neutral'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {page}
                      </button>
                    </div>
                  ))}
              </div>

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="min-h-11 sm:h-8 px-3 sm:px-2.5 text-sm rounded-md bg-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Balance Received Confirmation Modal for Wholesale Orders */}
      {showBalanceReceivedConfirmation && confirmOrderForBalanceReceived && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 mb-4">
                <svg className="h-6 w-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Confirm Balance Received</h3>
              <p className="text-sm text-gray-500 mb-4">
                Order: <strong>{safeRender(confirmOrderForBalanceReceived.orderNo)}</strong><br/>
                Customer: <strong>{safeRender(confirmOrderForBalanceReceived.customerName)}</strong>
              </p>
              <div className="bg-amber-50 p-4 rounded-lg mb-4 border border-amber-200">
                <p className="text-sm font-medium text-amber-800 mb-2">Mark this order as fully paid and completed?</p>
                <p className="text-sm text-amber-700">
                  Outstanding Balance: ₹{safeRender(confirmOrderForBalanceReceived.balance, 0).toLocaleString('en-IN')}
                </p>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => confirmWholesaleBalanceReceived(false)}
                  className="admin-btn admin-btn-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmWholesaleBalanceReceived(true)}
                  className="admin-btn admin-btn-modal-confirm admin-btn-warning"
                >
                  Yes, Balance Received
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dispatch Confirmation Modal for Wholesale Orders (balance due) */}
      {showWholesaleDispatchConfirmation && confirmOrderToDispatchWholesale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Dispatch Wholesale Order</h3>
              <p className="text-sm text-gray-500 mb-4">
                Order: <strong>{safeRender(confirmOrderToDispatchWholesale.orderNo)}</strong><br/>
                Customer: <strong>{safeRender(confirmOrderToDispatchWholesale.customerName)}</strong>
              </p>
              <div className="bg-purple-50 p-4 rounded-lg mb-4 border border-purple-200">
                <p className="text-sm font-medium text-purple-800 mb-2">Outstanding balance after dispatch</p>
                <p className="text-sm text-purple-700 mb-1">
                  Balance due: ₹{safeRender(confirmOrderToDispatchWholesale.balance, 0).toLocaleString('en-IN')}
                </p>
                <p className="text-sm text-purple-600">
                  Collect balance later using Balance Received to mark the order fully paid.
                </p>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => confirmDispatchWholesaleOrder(false)}
                  className="admin-btn admin-btn-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmDispatchWholesaleOrder(true)}
                  className="admin-btn admin-btn-modal-confirm admin-btn-neutral"
                >
                  Confirm Dispatch
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dispatch Confirmation Modal for Retail Orders */}
      {showDispatchConfirmation && confirmOrderToDispatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Dispatch Retail Order</h3>
              <p className="text-sm text-gray-500 mb-4">
                Order: <strong>{safeRender(confirmOrderToDispatch.orderNo)}</strong><br/>
                Customer: <strong>{safeRender(confirmOrderToDispatch.customerName)}</strong>
              </p>
              <div className="bg-orange-50 p-4 rounded-lg mb-4 border border-orange-200">
                <p className="text-sm font-medium text-orange-800 mb-2">⚠️ Dispatching will complete the order</p>
                {safeRender(confirmOrderToDispatch.balance) > 0 ? (
                  <div>
                    <p className="text-sm text-orange-700 mb-1">
                      Outstanding Balance: ₹{safeRender(confirmOrderToDispatch.balance).toLocaleString('en-IN')}
                    </p>
                    <p className="text-sm text-orange-600">
                      Balance will be marked as received and order will be completed upon dispatch.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-green-600">
                    ✅ Balance is already fully paid. Order will be completed upon dispatch.
                  </p>
                )}
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => confirmDispatchRetailOrder(false)}
                  className="admin-btn admin-btn-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmDispatchRetailOrder(true)}
                  className="admin-btn admin-btn-modal-confirm admin-btn-warning"
                >
                  Confirm Dispatch
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Confirmation Modal */}
      {showCancelConfirmation && confirmOrderToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Cancel Order</h3>
              <p className="text-sm text-gray-500 mb-4">
                Order: <strong>{safeRender(confirmOrderToCancel.orderNo)}</strong><br/>
                Customer: <strong>{safeRender(confirmOrderToCancel.customerName)}</strong>
              </p>
              <div className="bg-red-50 p-4 rounded-lg mb-4 border border-red-200">
                <p className="text-sm font-medium text-red-800 mb-2">⚠️ This action cannot be undone!</p>
                <p className="text-sm text-red-700 mb-2">
                  Cancelling this order will:
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>Mark the order as cancelled</li>
                  <li>Return all allocated items to inventory</li>
                  <li>Prevent further processing of this order</li>
                  {safeRender(confirmOrderToCancel.totalAdvance, 0) > 0 && (
                    <li>
                      Advance of ₹{safeRender(confirmOrderToCancel.totalAdvance, 0).toLocaleString('en-IN')} will be retained (non-refundable)
                    </li>
                  )}
                </ul>
                {safeRender(confirmOrderToCancel.items, []).length > 0 && (
                  <div className="mt-3 p-2 bg-white rounded border">
                    <p className="text-xs font-medium text-gray-700 mb-1">Items to be returned:</p>
                    {confirmOrderToCancel.items.map((item, index) => (
                      <p key={index} className="text-xs text-gray-600">
                        • {safeRender(item.modelName)} (Qty: {safeRender(item.qty)})
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => confirmCancelOrder(false)}
                  className="admin-btn admin-btn-modal-cancel"
                >
                  Keep Order
                </button>
                <button
                  onClick={() => confirmCancelOrder(true)}
                  className="admin-btn admin-btn-modal-confirm admin-btn-danger"
                >
                  Cancel Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Cancelled Order Confirmation Modal */}
      {showDeleteCancelledConfirmation && confirmOrderToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Cancelled Order</h3>
              <p className="text-sm text-gray-500 mb-4">
                Order: <strong>{safeRender(confirmOrderToDelete.orderNo)}</strong><br />
                Customer: <strong>{safeRender(confirmOrderToDelete.customerName)}</strong>
              </p>
              <div className="bg-red-50 p-4 rounded-lg mb-4 border border-red-200 text-left">
                <p className="text-sm font-medium text-red-800 mb-2">This permanently deletes the order.</p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>The order is removed from Cancelled and Finance</li>
                  {safeRender(confirmOrderToDelete.totalAdvance, 0) > 0 ? (
                    <li>
                      Advance of ₹{safeRender(confirmOrderToDelete.totalAdvance, 0).toLocaleString('en-IN')} will no longer be counted as retained (treated as refunded / not received)
                    </li>
                  ) : (
                    <li>No advance was recorded on this order</li>
                  )}
                  <li>Inventory was already returned when the order was cancelled — stock is not changed again</li>
                </ul>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  onClick={() => confirmDeleteCancelledOrder(false)}
                  disabled={deletingCancelled}
                  className="admin-btn admin-btn-modal-cancel disabled:opacity-50"
                >
                  Keep Order
                </button>
                <button
                  type="button"
                  onClick={() => confirmDeleteCancelledOrder(true)}
                  disabled={deletingCancelled}
                  className="admin-btn admin-btn-modal-confirm admin-btn-danger disabled:opacity-50"
                >
                  {deletingCancelled ? 'Deleting…' : 'Delete Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {showImageViewer && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowImageViewer(false);
                setSelectedImage(null);
              }}
              className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white min-h-11 min-w-11 flex items-center justify-center rounded-full hover:bg-opacity-70 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Image */}
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      <RetailOrderEditPanel
        order={editingOrder}
        open={Boolean(editingOrder)}
        onClose={() => {
          if (!editSaving) setEditingOrder(null);
        }}
        onSave={handleSaveRetailEdit}
        saving={editSaving}
      />
    </div>
  );
};

export default AdminOrdersPage;
